import { notEmpty } from "@product/scmp-sdk";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { useContentItemProviders } from "~/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import { useContentSponsor } from "~/components/content/content-sponsor-tag/hooks";
import type { Variant as TopicLinkVariant } from "~/components/content/content-topic-link";
import type { secondaryContentItemContent$key } from "~/queries/__generated__/secondaryContentItemContent.graphql";

import {
  Container,
  Content,
  Cover,
  Footer,
  Header,
  LiveTagContainer,
  StyledCoverEntityLink,
  StyledHeadlineEntityLink,
  TopicLinkContainer,
} from "./styles";

export type Props = {
  reference: secondaryContentItemContent$key;
  topicLinkVariant?: TopicLinkVariant;
} & ContentItemRenderVariantProps;

export const ContentItemSecondary = forwardRef<HTMLDivElement, Props>(
  ({ className, reference: reference_, topicLinkVariant }, reference) => {
    const entity = useFragment(
      graphql`
        fragment secondaryContentItemContent on Content {
          __typename
          ...entityLink
          ...hooksUseContentSponsorContent
          ...hooksContentItemProvidersContent
            @arguments(
              withLiveTag: true
              withTopicLink: true
              withHeadline: true
              withCoverImage: true
              withCoverImageSize540x360: true
              withCoverImageSize80x80: true
              withVideoDurationOverlay: true
              withSummary: true
              withActionBar: true
              withSponsorTag: true
            )
        }
      `,
      reference_,
    );
    const providers = useContentItemProviders(entity, topicLinkVariant);

    const header = (
      <>
        <LiveTagContainer>{providers.liveTag()}</LiveTagContainer>
        <TopicLinkContainer>{providers.topicLink?.()}</TopicLinkContainer>
        <StyledHeadlineEntityLink reference={entity}>
          {providers.headline({
            hideTooltip: true,
            preferSocialHeadline: true,
          })}
        </StyledHeadlineEntityLink>
      </>
    );
    const cover = (
      <StyledCoverEntityLink reference={entity}>
        {providers.coverImage({
          responsiveVariants: {
            desktopUp: "size540x360",
            mobileUp: "size80x80",
            tabletUp: "size540x360",
          },
        })}
        {providers.video?.durationOverlay()}
      </StyledCoverEntityLink>
    );
    const contentSponsor = useContentSponsor(entity);
    const isSponsorArticle = notEmpty(contentSponsor?.name);
    const content = isSponsorArticle ? null : providers.summary();
    const footer = isSponsorArticle
      ? providers.sponsorTag?.({
          color: "#001246",
          gap: "0px",
          layoutDirection: "vertical",
          nameFontSize: "12px",
          typeFontSize: "12px",
        })
      : providers.actionBar();

    return (
      <Container className={className} ref={reference}>
        <Header>{header}</Header>
        <Cover>{cover}</Cover>
        <Content>{content}</Content>
        <Footer>{footer}</Footer>
      </Container>
    );
  },
);

ContentItemSecondary.displayName = "ContentItemSecondary";
